<script lang="ts" setup>
import { computed, ref, useTemplateRef } from "vue";
import PickupInfoForm from "@/views/trips/components/forms/PickupInfoForm.vue";
import DeliveryInfoForm from "@/views/trips/components/forms/DeliveryInfoForm.vue";
import { OrderStopType } from "@/constants/order";
import type { StopInfoPipProps } from "@/types/props";

const props = withDefaults(defineProps<StopInfoPipProps>(), {
  preload: false,
  readonly: false,
});

const emits = defineEmits<{
  (e: "save"): void;
  (e: "close"): void;
}>();

const pickupFormRef = useTemplateRef<InstanceType<typeof PickupInfoForm> | null>("pickupForm");
const deliveryFormRef = useTemplateRef<InstanceType<typeof DeliveryInfoForm> | null>("deliveryForm");
const loading = ref<boolean>(false);

const isPickup = computed(() => props.stop.type === OrderStopType.pickup);

function save(): void {
  if (isPickup.value) {
    pickupFormRef.value?.save();

    return;
  }

  deliveryFormRef.value?.save();
}

function onSave(): void {
  emits("save");
  emits("close");
}
</script>

<template>
  <div class="stop-info-pip d-flex flex-column">
    <div class="stop-info-pip__header pa-6 pb-0 d-flex justify-space-between align-center">
      <div class="font-size-20 font-weight-500 text-grey-darken-4 line-height-24px text-wrap">
        {{ title }}
      </div>
    </div>

    <v-progress-linear :active="loading" color="primary" indeterminate />

    <div class="stop-info-pip__body pa-6 flex-grow-1 overflow-y-auto">
      <pickup-info-form
        v-if="isPickup"
        ref="pickupForm"
        v-model:loading="loading"
        :preload="preload"
        :readonly="readonly"
        :route="route"
        :stop="stop"
        :trip-id="tripId"
        @save="onSave"
      />
      <delivery-info-form
        v-else
        ref="deliveryForm"
        v-model:loading="loading"
        :preload="preload"
        :readonly="readonly"
        :route="route"
        :stop="stop"
        :trip-id="tripId"
        @save="onSave"
      />
    </div>

    <div class="stop-info-pip__footer pa-6 pt-4 d-flex justify-end">
      <v-btn
        :disabled="loading"
        :loading="loading"
        class="text-uppercase font-weight-600 font-size-14 px-4"
        color="primary"
        variant="flat"
        @click="save"
      >
        Save
      </v-btn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stop-info-pip {
  height: 100vh;

  &__body {
    min-height: 0;
  }
}
</style>
